import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import type { UserEntry } from '@common/types/users';
import { getPracticeAreaLabel } from '@common/util';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    FormControl,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    type Dispatch,
    forwardRef,
    type SetStateAction,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import SelectionCard from '@/components/SelectionCard';
import { createQuoteTemplate, updateQuoteTemplate } from '@/services/QuoteTemplatesService';
import {
    ALL_PROSPECT_PRACTICE_AREAS,
    getPracticeAreaMatterTypeOptions,
} from '@/utils/practiceAreaMatterTypes';
import { errorSnackbar, warningSnackbar } from '@/utils/snackbar';
import type { MatterTypeData } from './types';

interface MatterTypeErrors {
    quoteTemplateName?: string;
    quoteApproverId?: string;
}

interface Props {
    quoteTemplateId: number | null;
    users: UserEntry[];
    matterType: MatterTypeData;
    setMatterType: Dispatch<SetStateAction<MatterTypeData>>;
    selectedLawFirmId?: number;
    isTestFirm?: boolean;
    onCreate(quoteTemplateId: number): void;
}

const IOMatterType = forwardRef(function IOMatterType(
    {
        quoteTemplateId,
        users,
        matterType,
        setMatterType,
        selectedLawFirmId,
        isTestFirm = false,
        onCreate,
    }: Props,
    ref,
) {
    const theme = useTheme();
    const [showTip, setShowTip] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [matterTypeErrors, setMatterTypeErrors] = useState<MatterTypeErrors>();
    const handleTransactionTypeChange = (transactionType: ProspectMatterType) => {
        setIsDirty(true);
        setMatterType((prev) => ({
            ...prev,
            transactionType,
        }));
    };

    const submit = async (): Promise<{ success: boolean }> => {
        // Validation
        if (matterType.templateName.length === 0 || !matterType.assignedLawyer) {
            setMatterTypeErrors({
                quoteTemplateName:
                    matterType.templateName.length === 0 ? 'Template name is required' : undefined,
                quoteApproverId: !matterType.assignedLawyer
                    ? 'Please assign a user to this template'
                    : undefined,
            });
            errorSnackbar('Please fill all rquired fields', true);
            return { success: false };
        }

        const payload = {
            process: matterType.transactionType as ProspectMatterType,
            quoteTemplateName: matterType.templateName,
            quoteApproverId: matterType.assignedLawyer,
            templateType: 'IntakeOnly' as const,
            emailCC: matterType.emailCC,
            practiceArea: matterType.practiceArea as ProspectPracticeArea,
        };
        try {
            if (quoteTemplateId) {
                if (isDirty) {
                    await updateQuoteTemplate(quoteTemplateId, payload);
                }
            } else {
                const response = await createQuoteTemplate(payload);
                onCreate(response.data.quoteTemplateId);
            }
            return { success: true };
        } catch (error) {
            console.error(error);
            errorSnackbar('Something went wrong!');
            return { success: false };
        }
    };

    useImperativeHandle(ref, () => ({ submit }));

    const handleValueChange = (
        key: keyof MatterTypeData,
        value: string | string[] | number,
    ): void => {
        setIsDirty(true);
        setMatterTypeErrors(undefined);

        if (key === 'assignedLawyer') {
            const ccEmails = matterType.emailCC || [];
            // Check if the selected lawyer's userId matches any cc email in the list
            const newCCList = ccEmails.filter((email) => {
                const correspondingUser = users.find((user) => user.email === email);
                return !correspondingUser || correspondingUser.userId !== value;
            });
            if (
                ccEmails.length !== newCCList.length ||
                ccEmails.some((email, index) => email !== newCCList[index])
            ) {
                warningSnackbar(
                    'The CC member has been removed because they cannot be the same as the assigned user',
                );
            }
            setMatterType((prev) => ({
                ...prev,
                emailCC: newCCList,
                assignedLawyer: value as number,
            }));
        } else {
            setMatterType((prev) => ({
                ...prev,
                [key]: value,
            }));
        }
    };

    const practiceAreaMatterTypeOptions = getPracticeAreaMatterTypeOptions({
        lawFirmId: selectedLawFirmId,
        isTestFirm,
    });

    const selectedPracticeAreaMatterTypes = matterType.practiceArea
        ? practiceAreaMatterTypeOptions[matterType.practiceArea]
        : [];
    const matterTypeCardMaxWidth =
        selectedPracticeAreaMatterTypes.length >= 4 ? 'calc(25% - 18px)' : 'calc(33% - 12px)';

    const assignedLawyer = useMemo(() => {
        const lawyerId = Number(matterType.assignedLawyer) || 0;
        if (!lawyerId) return null;

        const selectedUser = users.find((user) => user.userId === lawyerId);
        if (!selectedUser) return null;

        return {
            label: selectedUser.fullName,
            value: selectedUser.userId,
            key: selectedUser.userId,
        };
    }, [matterType.assignedLawyer, users]);

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '24px',
                boxSizing: 'border-box',
                flexDirection: 'column',
                // (100vh - combined height, borders, paddings and margins of the header, footer and any margin, padding of the content area itself)
                maxHeight: 'calc(100vh - 168px)', // Todo: Move the heights of headers and footers to some sort of variables to better manage this
                minHeight: '400px',
                width: 'calc(100% - 64px)',
                padding: '32px',
                margin: '32px',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                backgroundColor: 'neutral.white',
                overflowY: 'auto',
            }}
        >
            <div>
                <Typography variant="h1" component="h1">
                    Matter Type
                </Typography>
                <Typography variant="body1" component="p">
                    Let's create your personalized intake form for specific matter type.
                </Typography>
            </div>
            {showTip && (
                <Alert
                    icon={<InfoSolid />}
                    severity="info"
                    onClose={() => {
                        setShowTip(false);
                    }}
                >
                    <AlertTitle>Tip</AlertTitle>
                    During the setup process, you will create an intake form for one of the
                    available matter types based on your selected practice area. Once the initial
                    setup is complete, you will have the ability to create new templates for other
                    types or adjust existing ones. Access the 'Templates' tab on your sidebar to
                    manage and customize your intake form templates.
                </Alert>
            )}
            <FormControl>
                <Typography variant="body1" sx={{ marginBottom: '10px' }}>
                    Select Practice Area
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <TextField
                            select
                            sx={{ width: '100%' }}
                            value={matterType.practiceArea || ''}
                            onChange={(event) => {
                                handleValueChange('practiceArea', event.target.value);
                            }}
                            label="Practice Area"
                            variant="outlined"
                            disabled={!!quoteTemplateId}
                        >
                            {ALL_PROSPECT_PRACTICE_AREAS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {getPracticeAreaLabel(option)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </FormControl>

            {matterType.practiceArea && (
                <>
                    <Typography variant="body1">
                        Choose {getPracticeAreaLabel(matterType.practiceArea).toLowerCase()} matter
                        type
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={3}>
                        {selectedPracticeAreaMatterTypes.map(
                            ({ value, disabled: optionDisabled }) => (
                                <SelectionCard
                                    key={value}
                                    isActive={matterType.transactionType === value}
                                    matterType={value}
                                    disabled={
                                        // A saved template's matter type can't change
                                        (!!quoteTemplateId &&
                                            matterType.transactionType !== value) ||
                                        optionDisabled
                                    }
                                    maxWidth={matterTypeCardMaxWidth}
                                    onClick={() => handleTransactionTypeChange(value)}
                                />
                            ),
                        )}
                    </Stack>
                </>
            )}
            <Typography variant="body1" component="p">
                Name your template and assign a lawyer to it
            </Typography>
            <Stack direction="row" spacing={3}>
                <TextField
                    value={matterType.templateName}
                    onChange={(ev) => {
                        handleValueChange('templateName', ev.target.value);
                    }}
                    fullWidth
                    error={!!matterTypeErrors?.quoteTemplateName}
                    helperText={matterTypeErrors?.quoteTemplateName}
                    placeholder="e.g. Sale intake template"
                    type="text"
                    variant="outlined"
                    label="Template name"
                />
                <Autocomplete
                    value={assignedLawyer}
                    onChange={(_ev, values) => {
                        handleValueChange('assignedLawyer', values?.value ?? 0);
                    }}
                    fullWidth
                    disablePortal
                    options={users.map((user) => ({
                        label: user.fullName,
                        value: user.userId,
                        key: user.userId,
                    }))}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    renderInput={(params) => {
                        const newParams = { ...params };
                        if (params.inputProps.value === '[object Object]') {
                            newParams.inputProps.value = '';
                        }
                        return (
                            <TextField
                                {...newParams}
                                error={!!matterTypeErrors?.quoteApproverId}
                                helperText={matterTypeErrors?.quoteApproverId}
                                label="Assigned user"
                            />
                        );
                    }}
                />
                <AutoCompleteMultiSelect
                    fullWidth
                    getOptionLabel={(options) => options}
                    label="Select CC persons"
                    value={matterType.emailCC}
                    options={users
                        ?.filter((u) => u.userId !== matterType.assignedLawyer)
                        ?.map((user) => user.email)}
                    onChange={(_, value) => handleValueChange('emailCC', value)}
                />
            </Stack>
        </Box>
    );
});

export default IOMatterType;
